/**
 * Helpers the copilot components share. Not exported from the index: they are
 * implementation details of this folder, not UI anyone else composes.
 */
import { debug } from '../../lib/debug';

/* Stable fallbacks, so a component rendered outside the provider (a test, a
   screen mounted on its own) neither crashes on a missing key nor hands a
   fresh array to a dependency list on every render. */
export const NO_MESSAGES = Object.freeze([]);
export const NO_CAPTION = Object.freeze({ user: '', agent: '' });

/**
 * Call a provider action without letting it break the click that called it.
 *
 * Every action on the copilot context is either synchronous or returns a
 * promise, and outside the provider some are missing entirely. A rejected
 * promise from a button handler is an unhandled rejection in the console and
 * nothing on screen — the provider already turns failures into lines in the
 * thread — so here it is only traced.
 *
 * Deliberately NOT deferred: `unlockAudio` must run inside the user gesture
 * that allowed it, and a microtask hop can lose that gesture in Safari.
 */
export function callSafely(fn, ...args) {
  if (typeof fn !== 'function') return undefined;
  try {
    const out = fn(...args);
    if (out && typeof out.then === 'function') {
      out.then(undefined, (err) => debug('copilot/ui', { action: fn.name || 'action', error: err?.message }));
    }
    return out;
  } catch (err) {
    debug('copilot/ui', { action: fn.name || 'action', error: err?.message });
    return undefined;
  }
}

/**
 * The newest part of a line that is still growing.
 *
 * A caption is read at its end — the words being said now — but a clamped box
 * shows the beginning. Cutting at a word boundary from the front keeps what
 * matters on screen without a scroll container per caption.
 */
export function tail(text, max = 160) {
  const s = String(text ?? '').replace(/\s+/g, ' ').trim();
  if (s.length <= max) return s;
  const cut = s.slice(s.length - max);
  const space = cut.indexOf(' ');
  return `…${space > 0 && space < 24 ? cut.slice(space + 1) : cut}`;
}

/** What each connection state is called wherever it is named to the reader. */
export const CONNECTION_LABEL = {
  idle: 'Not connected yet',
  minting: 'Connecting…',
  connecting: 'Connecting…',
  waiting_agent: 'Connected — waiting for the assistant to join',
  ready: 'Ready',
  reconnecting: 'Reconnecting…',
  retrying: 'Reconnecting…',
  disconnected: 'Disconnected',
  failed: 'Could not connect',
};

/**
 * Whether the connection has stopped trying on its own, so a Reconnect button is the way on.
 * 'retrying' (a backoff wait) is not down: the app reconnects by itself when it ends.
 */
export const isDown = (connection) => connection === 'failed' || connection === 'disconnected';

/**
 * Whether a surface should offer Reconnect in its header: the connection gave
 * up, or the room stayed empty after its fresh token (`stalled`).
 */
export const offersReconnect = (connection, stalled) => isDown(connection) || Boolean(stalled);

/** `.chatpane-dot` modifier for a connection state: the honest on / wait / off. */
export function dotClass(connection) {
  if (connection === 'ready') return 'on';
  if (isDown(connection)) return 'off';
  return 'wait';
}

/**
 * The resting line a thread opens with, per connection state.
 *
 * Stated rather than implied: a panel that looks ready and answers nothing is
 * the state worth naming. `ready` is the caller's own sentence, because the
 * dock and the drawer greet differently.
 */
export function restingText(connection, ready) {
  switch (connection) {
    case 'ready':
      return ready;
    case 'reconnecting':
    case 'retrying':
      return 'Reconnecting… A question asked now is held for a minute and sent when the assistant is back.';
    case 'waiting_agent':
      return 'Connected — waiting for the assistant to join the room.';
    case 'failed':
    case 'disconnected':
      return 'The assistant could not connect. Everything else on screen is live — click any number for its history, or run the Analysis Studio.';
    default:
      return 'Connecting…';
  }
}

/** A single-line, human reading of a value the agent supplied for an action. */
export function describeValue(value) {
  if (value == null || value === '') return '';
  if (Array.isArray(value)) return value.map(describeValue).filter(Boolean).join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return '';
    }
  }
  return String(value);
}

/** `securitySymbol` → "Security symbol", for an inputs summary nobody wrote labels for. */
export function humanizeKey(key) {
  const words = String(key)
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase();
  return words ? words[0].toUpperCase() + words.slice(1) : '';
}

/**
 * Whether the viewport is the phone layout, where the dock and the drawer are
 * full-screen sheets. Read at the moment of the click rather than tracked: it
 * decides one thing — whether following a link should uncover the page.
 */
export function isSheetLayout() {
  try {
    return !!window.matchMedia?.('(max-width: 768px)')?.matches;
  } catch {
    return false;
  }
}
